import { MlTextRecord } from 'src/app/Theme/shared/service/ml-description.service';
import {
  normalizeMediaUrlForDisplay,
  resolveCarouselImageDisplayUrl,
  resolveCarouselImageUrl,
} from 'src/app/Theme/shared/utils/carousel-image-url.utils';
import {
  ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY,
  AllProductsViewPorts,
  parseAllProductsViewPorts,
  parseDefaultViewPortKey,
  parseViewPortOrder,
  ViewPortKey,
} from './our-products-layout.util';
import {
  CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY,
  CATEGORY_LAYOUT_PHONE_MAX_PX,
  CATEGORY_LAYOUT_PHONE_MIN_PX,
  CATEGORY_LAYOUT_PHONE_MEDIA_QUERY,
  resolveCategoryLayoutDeviceFromViewport,
} from './category-layout.util';

export const PRODUCTS_PAGE_NAME_PAYLOAD_KEY = 'pageName';
/** Mobile-specific page title; falls back to desktop `pageName` when absent. */
export const PRODUCTS_PAGE_NAME_MOBILE_PAYLOAD_KEY = 'pageNameMobile';
/** Mobile-specific view ports; falls back to desktop `allProductsViewPorts` when absent. */
export const PRODUCTS_VIEW_PORTS_MOBILE_PAYLOAD_KEY = 'allProductsViewPortsMobile';

export type ProductsPageLayoutDevice = 'desktop' | 'mobile';

export {
  CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY,
  CATEGORY_LAYOUT_PHONE_MAX_PX,
  CATEGORY_LAYOUT_PHONE_MIN_PX,
  CATEGORY_LAYOUT_PHONE_MEDIA_QUERY,
};

export function resolveProductsPageLayoutDeviceFromViewport(
  win: Window | null = typeof window !== 'undefined' ? window : null,
): ProductsPageLayoutDevice {
  return resolveCategoryLayoutDeviceFromViewport(win);
}

export function productsPageNamePayloadKey(
  device: ProductsPageLayoutDevice = 'desktop',
): string {
  return device === 'mobile'
    ? PRODUCTS_PAGE_NAME_MOBILE_PAYLOAD_KEY
    : PRODUCTS_PAGE_NAME_PAYLOAD_KEY;
}

export function productsViewPortsPayloadKey(
  device: ProductsPageLayoutDevice = 'desktop',
): string {
  return device === 'mobile'
    ? PRODUCTS_VIEW_PORTS_MOBILE_PAYLOAD_KEY
    : ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY;
}

export function productsExplorePayloadKey(
  device: ProductsPageLayoutDevice = 'desktop',
): string {
  return device === 'mobile'
    ? PRODUCTS_PAGE_EXPLORE_MOBILE_PAYLOAD_KEY
    : PRODUCTS_PAGE_EXPLORE_PAYLOAD_KEY;
}

function payloadForProductsDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ProductsPageLayoutDevice,
): Record<string, unknown> | null | undefined {
  if (!payload || device === 'desktop') {
    return payload;
  }
  const mobile = payload[PRODUCTS_VIEW_PORTS_MOBILE_PAYLOAD_KEY];
  if (mobile && typeof mobile === 'object' && !Array.isArray(mobile)) {
    return { [ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY]: mobile };
  }
  return payload;
}

export function parseProductsPageViewPortsForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ProductsPageLayoutDevice = 'desktop',
): AllProductsViewPorts {
  return parseAllProductsViewPorts(payloadForProductsDevice(payload, device));
}

export function parseProductsPageViewPortOrderForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ProductsPageLayoutDevice = 'desktop',
): ViewPortKey[] {
  const order = parseViewPortOrder(payloadForProductsDevice(payload, device), PRODUCTS_PAGE_VIEW_PORT_KEYS);
  return order.filter((key) => PRODUCTS_PAGE_VIEW_PORT_KEYS.includes(key));
}

export function parseProductsPageDefaultViewPortForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ProductsPageLayoutDevice = 'desktop',
): ViewPortKey {
  const devicePayload = payloadForProductsDevice(payload, device);
  return parseDefaultViewPortKey(
    devicePayload,
    parseAllProductsViewPorts(devicePayload),
    PRODUCTS_PAGE_VIEW_PORT_KEYS,
  );
}

export function parseProductsPageNameForDevice(
  payload: Record<string, unknown> | null | undefined,
  preferredLang?: string,
  device: ProductsPageLayoutDevice = 'desktop',
): string {
  if (
    device === 'mobile' &&
    payload &&
    Object.prototype.hasOwnProperty.call(payload, PRODUCTS_PAGE_NAME_MOBILE_PAYLOAD_KEY)
  ) {
    return resolveMlText(
      parseMlRecord(payload[PRODUCTS_PAGE_NAME_MOBILE_PAYLOAD_KEY]),
      preferredLang,
    );
  }
  return resolveMlText(parseMlRecord(payload?.[PRODUCTS_PAGE_NAME_PAYLOAD_KEY]), preferredLang);
}

export const PRODUCTS_PAGE_BANNER_PAYLOAD_KEY = 'banner';
/** Mobile banner. Kept separate from desktop `banner`; missing means no mobile banner. */
export const PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY = 'bannerMobile';
export const PRODUCTS_PAGE_EXPLORE_PAYLOAD_KEY = 'exploreProducts';
/** Mobile-specific explore section; falls back to desktop `exploreProducts` when absent. */
export const PRODUCTS_PAGE_EXPLORE_MOBILE_PAYLOAD_KEY = 'exploreProductsMobile';
export const PRODUCTS_PAGE_RECENTLY_ADDED_PAYLOAD_KEY = 'recentlyAdded';
export const PRODUCTS_PAGE_PRODUCTS_VISIBLE_PAYLOAD_KEY = 'productsVisible';

export const PRODUCTS_PAGE_VIEW_PORT_KEYS: ViewPortKey[] = [
  'verticalCard',
  'horizontalCard',
  'tableView',
];

export type ProductsPageBannerContentType = 'image' | 'product';
export type ProductsPageBannerDisplayType = 'regular' | 'carousel';
export type ProductsPageBannerScope = 'thisPage' | 'allPages' | 'selectedPages';

export const PRODUCTS_PAGE_BANNER_SCOPE_PAGE_KEYS = [
  'customerDashboard',
  'newsPage',
  'allPage',
  'productPage',
] as const;

export type ProductsPageBannerScopePageKey = (typeof PRODUCTS_PAGE_BANNER_SCOPE_PAGE_KEYS)[number];

const EMPTY_ML: MlTextRecord = {};

export const PRODUCTS_PAGE_BANNER_CAROUSEL_MIN_SLIDES = 3;
export const PRODUCTS_PAGE_BANNER_CAROUSEL_MAX_SLIDES = 5;

export interface ProductsPageBannerCarouselSlide {
  image: string;
  /** Raw API/editor image value when {@link image} resolved empty at parse time. */
  imagePayload?: unknown;
  /** Published product item code when banner content is product. */
  productItemCode: string;
  title: MlTextRecord;
  description: MlTextRecord;
  cta: MlTextRecord;
  titleEnabled: boolean;
  descriptionEnabled: boolean;
  ctaEnabled: boolean;
}

export function createDefaultProductsPageBannerCarouselSlide(): ProductsPageBannerCarouselSlide {
  return {
    image: '',
    productItemCode: '',
    title: { ...EMPTY_ML },
    description: { ...EMPTY_ML },
    cta: { ...EMPTY_ML },
    titleEnabled: false,
    descriptionEnabled: false,
    ctaEnabled: false,
  };
}

export function createDefaultProductsPageBannerCarouselSlides(
  count = PRODUCTS_PAGE_BANNER_CAROUSEL_MIN_SLIDES,
): ProductsPageBannerCarouselSlide[] {
  return Array.from({ length: count }, () => createDefaultProductsPageBannerCarouselSlide());
}

const BANNER_PLACEHOLDER_IMAGE_PATH = 'assets/images/default.jpg';

/**
 * Image address that would actually show in the slide upload box.
 * Empty values, "none", and the placeholder image do not count.
 */
export function bannerCarouselSlideDisplayImage(image: unknown): string {
  const url = resolveCarouselImageDisplayUrl(image).trim();
  if (!url || url.toLowerCase() === 'none') {
    return '';
  }
  const path = url.split('?')[0].split('#')[0].replace(/\\/g, '/').toLowerCase();
  if (path.endsWith(`/${BANNER_PLACEHOLDER_IMAGE_PATH}`) || path.endsWith(BANNER_PLACEHOLDER_IMAGE_PATH)) {
    return '';
  }
  return url;
}

/** Image slides need a visible picture. Product slides need a product and a visible picture. */
export function isBannerCarouselSlideFilled(
  slide: { image?: unknown; productItemCode?: string | null },
  mode: 'image' | 'product',
): boolean {
  const hasImage = !!bannerCarouselSlideDisplayImage(slide.image);
  if (mode === 'product') {
    return !!(slide.productItemCode ?? '').trim() && hasImage;
  }
  return hasImage;
}

/** A carousel can be saved only when every slide is filled and there are at least three. */
export function bannerCarouselSlidesAreReadyToSave(
  slides: Array<{ image?: unknown; productItemCode?: string | null }>,
  mode: 'image' | 'product',
): boolean {
  if (slides.length < PRODUCTS_PAGE_BANNER_CAROUSEL_MIN_SLIDES) {
    return false;
  }
  return slides.every((slide) => isBannerCarouselSlideFilled(slide, mode));
}

export function normalizeProductsPageBannerCarouselSlides(
  slides: ProductsPageBannerCarouselSlide[],
): ProductsPageBannerCarouselSlide[] {
  const next = slides.map((slide) => ({
    ...slide,
    title: { ...slide.title },
    description: { ...slide.description },
    cta: { ...slide.cta },
  }));
  while (next.length < PRODUCTS_PAGE_BANNER_CAROUSEL_MIN_SLIDES) {
    next.push(createDefaultProductsPageBannerCarouselSlide());
  }
  return next.slice(0, PRODUCTS_PAGE_BANNER_CAROUSEL_MAX_SLIDES);
}

export const PRODUCTS_PAGE_BANNER_SCOPE_PAGE_OPTIONS: ReadonlyArray<{
  key: ProductsPageBannerScopePageKey;
  labelKey: string;
}> = [
  {
    key: 'customerDashboard',
    labelKey: 'ADCustomerCustomize.ProductsPageBannerScopePageCustomerDashboard',
  },
  { key: 'newsPage', labelKey: 'ADCustomerCustomize.ProductsPageBannerScopePageNews' },
  { key: 'allPage', labelKey: 'ADCustomerCustomize.ProductsPageBannerScopePageAll' },
  { key: 'productPage', labelKey: 'ADCustomerCustomize.ProductsPageBannerScopePageProduct' },
];

export interface ProductsPageBannerConfig {
  visible: boolean;
  contentType: ProductsPageBannerContentType;
  displayType: ProductsPageBannerDisplayType;
  scope: ProductsPageBannerScope;
  /** When scope is `selectedPages`, subset of {@link PRODUCTS_PAGE_BANNER_SCOPE_PAGE_KEYS}. */
  selectedPageKeys: ProductsPageBannerScopePageKey[];
  image: string;
  /** Published product item code when content is product + regular banner. */
  productItemCode: string;
  title: MlTextRecord;
  description: MlTextRecord;
  cta: MlTextRecord;
  /** When false, title is not shown on the customer banner (default off). */
  titleEnabled: boolean;
  descriptionEnabled: boolean;
  ctaEnabled: boolean;
  carouselSlides: ProductsPageBannerCarouselSlide[];
}

export interface ProductsPageExploreConfig {
  visible: boolean;
  title: MlTextRecord;
}

export interface ProductsPageRecentlyAddedConfig {
  /** Hides the recently added tab (and panel) on the customer products page. */
  visible: boolean;
  defaultOpen: boolean;
  title: MlTextRecord;
}

export interface ProductsPageLayoutConfig {
  pageName: MlTextRecord;
  banner: ProductsPageBannerConfig;
  exploreProducts: ProductsPageExploreConfig;
  recentlyAdded: ProductsPageRecentlyAddedConfig;
  productsVisible: boolean;
  allProductsViewPorts: AllProductsViewPorts;
  /** Default view-port key persisted under `allProductsViewPorts.default`. */
  allProductsViewPortsDefault: ViewPortKey;
  /** Display order persisted under `allProductsViewPorts.order`. */
  allProductsViewPortsOrder: ViewPortKey[];
}

export const DEFAULT_PRODUCTS_PAGE_BANNER: ProductsPageBannerConfig = {
  visible: true,
  contentType: 'image',
  displayType: 'regular',
  scope: 'thisPage',
  selectedPageKeys: [],
  image: '',
  productItemCode: '',
  title: { ...EMPTY_ML },
  description: { ...EMPTY_ML },
  cta: { ...EMPTY_ML },
  titleEnabled: false,
  descriptionEnabled: false,
  ctaEnabled: false,
  carouselSlides: [] as ProductsPageBannerCarouselSlide[],
};

function parseBannerCarouselSlides(value: unknown): ProductsPageBannerCarouselSlide[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return createDefaultProductsPageBannerCarouselSlide();
    }
    const record = entry as Record<string, unknown>;
    const slideImageSource =
      record['image'] ??
      record['imageUrl'] ??
      record['image_url'] ??
      record['previewUrl'] ??
      record['cf_image'] ??
      record;
    const resolvedImage = resolveCarouselImageUrl(slideImageSource);
    return {
      image: resolvedImage,
      imagePayload: resolvedImage ? undefined : slideImageSource,
      productItemCode:
        typeof record['productItemCode'] === 'string' ? record['productItemCode'].trim() : '',
      title: parseMlRecord(record['title']),
      description: parseMlRecord(record['description']),
      cta: parseMlRecord(record['cta']),
      titleEnabled: record['titleEnabled'] === true,
      descriptionEnabled: record['descriptionEnabled'] === true,
      ctaEnabled: record['ctaEnabled'] === true,
    };
  });
}

function parseBannerSelectedPageKeys(value: unknown): ProductsPageBannerScopePageKey[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const allowed = new Set<string>(PRODUCTS_PAGE_BANNER_SCOPE_PAGE_KEYS);
  const keys: ProductsPageBannerScopePageKey[] = [];
  for (const entry of value) {
    if (typeof entry === 'string' && allowed.has(entry)) {
      const key = entry as ProductsPageBannerScopePageKey;
      if (!keys.includes(key)) {
        keys.push(key);
      }
    }
  }
  return keys;
}

function parseBannerContentType(value: unknown): ProductsPageBannerContentType {
  return value === 'product' ? 'product' : 'image';
}

function readBannerCarouselSlidesRaw(record: Record<string, unknown>): unknown {
  return record['carouselSlides'] ?? record['carousel_slides'] ?? record['slides'];
}

function bannerRecordHasCarouselSlides(record: Record<string, unknown>): boolean {
  const slides = parseBannerCarouselSlides(readBannerCarouselSlidesRaw(record));
  return slides.some((slide) => resolveBannerSlideImageUrl(slide).length > 0);
}

function parseBannerDisplayType(
  value: unknown,
  record: Record<string, unknown>,
): ProductsPageBannerDisplayType {
  if (value === 'carousel' || value === 'regular') {
    return value;
  }
  if (bannerRecordHasCarouselSlides(record)) {
    return 'carousel';
  }
  return 'regular';
}

function parseBannerScope(value: unknown): ProductsPageBannerScope {
  if (value === 'allPages' || value === 'selectedPages') {
    return value;
  }
  return 'thisPage';
}

/** Page checkboxes apply only while "Selected pages" is the active choice. */
function selectedPageKeysForScope(
  scope: ProductsPageBannerScope,
  keys: ProductsPageBannerScopePageKey[],
): ProductsPageBannerScopePageKey[] {
  return scope === 'selectedPages' ? keys : [];
}

/** The page being edited stays checked while Selected pages is active. */
export function withRequiredBannerScopePage(
  keys: ProductsPageBannerScopePageKey[],
  requiredKey: ProductsPageBannerScopePageKey,
): ProductsPageBannerScopePageKey[] {
  const selected = new Set(keys);
  selected.add(requiredKey);
  return PRODUCTS_PAGE_BANNER_SCOPE_PAGE_KEYS.filter((key) => selected.has(key));
}

export const DEFAULT_PRODUCTS_PAGE_EXPLORE: ProductsPageExploreConfig = {
  visible: true,
  title: { ...EMPTY_ML },
};

export const DEFAULT_PRODUCTS_PAGE_RECENTLY_ADDED: ProductsPageRecentlyAddedConfig = {
  visible: true,
  /** Collapsed unless an admin opts in: the page opens with the tab only. */
  defaultOpen: false,
  title: { ...EMPTY_ML },
};

export const DEFAULT_PRODUCTS_PAGE_LAYOUT: ProductsPageLayoutConfig = {
  pageName: { ...EMPTY_ML },
  banner: { ...DEFAULT_PRODUCTS_PAGE_BANNER },
  exploreProducts: { ...DEFAULT_PRODUCTS_PAGE_EXPLORE },
  recentlyAdded: { ...DEFAULT_PRODUCTS_PAGE_RECENTLY_ADDED },
  productsVisible: true,
  allProductsViewPorts: {
    verticalCard: true,
    horizontalCard: true,
    tableView: true,
  },
  allProductsViewPortsDefault: 'verticalCard',
  allProductsViewPortsOrder: [...PRODUCTS_PAGE_VIEW_PORT_KEYS],
};

function parseMlRecord(value: unknown): MlTextRecord {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? { en: trimmed } : {};
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const record: MlTextRecord = {};
  for (const [key, text] of Object.entries(value as Record<string, unknown>)) {
    if (typeof text === 'string' && text.trim()) {
      record[key] = text.trim();
    }
  }
  return record;
}

/** Normalize resolve-batch / layout API payload before reading products-page keys. */
export function normalizeProductsPagePortalPayload(
  payload: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }
  const nestedLayout = payload['layout'];
  if (nestedLayout && typeof nestedLayout === 'object' && !Array.isArray(nestedLayout)) {
    const layoutRecord = nestedLayout as Record<string, unknown>;
    const nestedPayload = layoutRecord['payload'];
    if (nestedPayload && typeof nestedPayload === 'object' && !Array.isArray(nestedPayload)) {
      return coalesceProductsPageBannerPayloadKeys({
        ...payload,
        ...(nestedPayload as Record<string, unknown>),
      });
    }
  }
  const nestedPayload = payload['payload'];
  if (nestedPayload && typeof nestedPayload === 'object' && !Array.isArray(nestedPayload)) {
    return coalesceProductsPageBannerPayloadKeys({
      ...(nestedPayload as Record<string, unknown>),
      ...payload,
    });
  }
  return coalesceProductsPageBannerPayloadKeys(payload);
}

function toBannerPayloadRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

/** Prefer banner payload records that actually contain carousel/image data (resolve-batch vs saved layout). */
function mergeProductsPageBannerPayloadRecord(
  primary: unknown,
  fallback: unknown,
): Record<string, unknown> {
  const primaryRecord = toBannerPayloadRecord(primary);
  const fallbackRecord = toBannerPayloadRecord(fallback);
  if (!primaryRecord) {
    return fallbackRecord ?? {};
  }
  if (!fallbackRecord) {
    return primaryRecord;
  }
  // An explicit per-device save, including Reset, must not be replaced by an older carousel.
  if (primaryRecord['deviceImageConfigured'] === true) {
    return primaryRecord;
  }
  const primaryConfig = parseBannerConfig(primaryRecord);
  const fallbackConfig = parseBannerConfig(fallbackRecord);
  const primaryRich = productsPageBannerHasConfiguredVisual(primaryConfig);
  const fallbackRich = productsPageBannerHasConfiguredVisual(fallbackConfig);
  if (!primaryRich && fallbackRich) {
    return { ...primaryRecord, ...fallbackRecord };
  }
  if (primaryRich && !fallbackRich) {
    return { ...fallbackRecord, ...primaryRecord };
  }
  const mergedConfig: ProductsPageBannerConfig = {
    ...fallbackConfig,
    ...primaryConfig,
    carouselSlides: carouselSlidesHaveDisplayableImage(primaryConfig.carouselSlides)
      ? primaryConfig.carouselSlides
      : fallbackConfig.carouselSlides,
    image: primaryConfig.image.trim() ? primaryConfig.image : fallbackConfig.image,
    displayType: primaryConfig.displayType ?? fallbackConfig.displayType,
    contentType: primaryConfig.contentType || fallbackConfig.contentType,
  };
  return productsPageBannerToPayloadRecord(mergedConfig, {
    deviceImageConfigured:
      primaryRecord['deviceImageConfigured'] === true ||
      fallbackRecord['deviceImageConfigured'] === true,
  });
}

function coalesceProductsPageBannerPayloadKeys(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  return { ...payload };
}

/** Keep richer products-page banner data when resolve refresh returns a thinner payload than the session cache. */
export function mergeProductsPageResolvedPayload(
  previous: Record<string, unknown> | null | undefined,
  next: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const prevFlat = normalizeProductsPagePortalPayload(previous ?? {});
  const nextFlat = normalizeProductsPagePortalPayload(next ?? {});
  const merged: Record<string, unknown> = { ...prevFlat, ...nextFlat };
  merged[PRODUCTS_PAGE_BANNER_PAYLOAD_KEY] = mergeProductsPageBannerPayloadRecord(
    nextFlat[PRODUCTS_PAGE_BANNER_PAYLOAD_KEY],
    prevFlat[PRODUCTS_PAGE_BANNER_PAYLOAD_KEY],
  );
  merged[PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY] = mergeProductsPageBannerPayloadRecord(
    nextFlat[PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY],
    prevFlat[PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY],
  );
  return coalesceProductsPageBannerPayloadKeys(merged);
}

function parseBannerConfig(value: unknown): ProductsPageBannerConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_PRODUCTS_PAGE_BANNER };
  }
  const record = value as Record<string, unknown>;
  const image = resolveCarouselImageUrl(
    record['image'] ?? record['imageUrl'] ?? record['image_url'] ?? record['bannerImage'],
  );
  const scope = parseBannerScope(record['scope']);
  return {
    visible: record['visible'] !== false,
    contentType: parseBannerContentType(record['contentType']),
    displayType: parseBannerDisplayType(
      record['displayType'] ?? record['display_type'],
      record,
    ),
    scope,
    selectedPageKeys: selectedPageKeysForScope(
      scope,
      parseBannerSelectedPageKeys(record['selectedPageKeys']),
    ),
    image,
    productItemCode:
      typeof record['productItemCode'] === 'string' ? record['productItemCode'].trim() : '',
    title: parseMlRecord(record['title']),
    description: parseMlRecord(record['description']),
    cta: parseMlRecord(record['cta']),
    titleEnabled: record['titleEnabled'] === true,
    descriptionEnabled: record['descriptionEnabled'] === true,
    ctaEnabled: record['ctaEnabled'] === true,
    carouselSlides: parseBannerCarouselSlides(readBannerCarouselSlidesRaw(record)),
  };
}

export function isProductsPageBannerImageRegular(config: ProductsPageBannerConfig): boolean {
  return config.contentType === 'image' && config.displayType === 'regular';
}

export function isProductsPageBannerImageCarousel(config: ProductsPageBannerConfig): boolean {
  return config.contentType === 'image' && config.displayType === 'carousel';
}

export function isProductsPageBannerProductCarousel(config: ProductsPageBannerConfig): boolean {
  return config.contentType === 'product' && config.displayType === 'carousel';
}

export function isProductsPageBannerProductRegular(config: ProductsPageBannerConfig): boolean {
  return config.contentType === 'product' && config.displayType === 'regular';
}

export function isProductsPageBannerCarousel(config: ProductsPageBannerConfig): boolean {
  if (config.displayType === 'regular') {
    return false;
  }
  if (isProductsPageBannerImageCarousel(config) || isProductsPageBannerProductCarousel(config)) {
    return true;
  }
  if (config.displayType === 'carousel') {
    return true;
  }
  return (config.carouselSlides ?? []).some(
    (slide) => resolveBannerSlideImageUrl(slide).length > 0,
  );
}

/** Whether the saved products-page banner should render on the customer products catalog. */
export function isProductsPageBannerActiveOnProductPage(config: ProductsPageBannerConfig): boolean {
  if (config.scope === 'allPages' || config.scope === 'thisPage') {
    return true;
  }
  if (config.scope === 'selectedPages') {
    return (
      config.selectedPageKeys.includes('productPage') ||
      config.selectedPageKeys.includes('allPage')
    );
  }
  return false;
}

/** Whether the saved dashboard-layout banner should render on the customer dashboard. */
export function isProductsPageBannerActiveOnCustomerDashboard(
  config: ProductsPageBannerConfig,
): boolean {
  if (config.scope === 'allPages' || config.scope === 'thisPage') {
    return true;
  }
  if (config.scope === 'selectedPages') {
    return (
      config.selectedPageKeys.includes('customerDashboard') ||
      config.selectedPageKeys.includes('allPage')
    );
  }
  return false;
}

/** Product item code used for product-based banner modes (regular or first carousel slide). */
export function resolveProductsPageBannerProductItemCode(
  config: ProductsPageBannerConfig,
): string {
  if (isProductsPageBannerProductRegular(config)) {
    return config.productItemCode.trim();
  }
  if (isProductsPageBannerProductCarousel(config)) {
    const slides = config.carouselSlides ?? [];
    const withCode = slides.find((slide) => slide.productItemCode.trim());
    return (withCode ?? slides[0])?.productItemCode.trim() ?? '';
  }
  return '';
}

export function resolveProductsPageBannerImageUrl(config: ProductsPageBannerConfig): string {
  if (isProductsPageBannerImageCarousel(config) || isProductsPageBannerProductCarousel(config)) {
    const slides = config.carouselSlides ?? [];
    const slideWithImage = slides.find((slide) => resolveBannerSlideImageUrl(slide));
    const fallbackSlide = slides[0];
    if (slideWithImage) {
      return resolveBannerSlideImageUrl(slideWithImage);
    }
    return fallbackSlide ? resolveBannerSlideImageUrl(fallbackSlide) : '';
  }
  return resolveCarouselImageUrl(config.image);
}

export interface ProductsPageCustomerBannerView {
  visible: boolean;
  scopeActive: boolean;
  show: boolean;
  imageUrl: string;
  title: string;
  description: string;
  cta: string;
  showTitle: boolean;
  showDescription: boolean;
  showCta: boolean;
}

export interface ProductsPageCustomerBannerCarouselSlide {
  imageUrl: string;
  /** Set for product carousel slides; customer UI loads the product hero image when `imageUrl` is empty. */
  productItemCode?: string;
  title: string;
  description: string;
  cta: string;
  showTitle: boolean;
  showDescription: boolean;
  showCta: boolean;
}

export const DEFAULT_PRODUCTS_PAGE_BANNER_FALLBACK_IMAGE = '/assets/images/default.jpg';

/** True when admin saved a banner image, carousel slide, or product-based hero. */
export function productsPageBannerHasConfiguredVisual(config: ProductsPageBannerConfig): boolean {
  if (carouselSlidesHaveDisplayableImage(config.carouselSlides ?? [])) {
    return true;
  }
  if (resolveCarouselImageUrl(config.image).trim()) {
    return true;
  }
  return !!resolveProductsPageBannerProductItemCode(config).trim();
}

function resolveProductsPageBannerHeroImageUrl(config: ProductsPageBannerConfig): string {
  const slides = config.carouselSlides ?? [];
  const slideWithImage = slides.find((slide) => resolveBannerSlideImageUrl(slide));
  const slideHero = slideWithImage ? resolveBannerSlideImageUrl(slideWithImage) : '';
  if (isProductsPageBannerImageCarousel(config)) {
    return slideHero;
  }
  const bannerImage = resolveCarouselImageUrl(config.image).trim();
  if (bannerImage) {
    return bannerImage;
  }
  return slideHero;
}

/** Customer carousel slides (image or product carousel) with per-slide overlay text. */
export function resolveProductsPageCustomerBannerCarouselSlides(
  config: ProductsPageBannerConfig,
  preferredLang?: string,
  fallbackImage = '',
): ProductsPageCustomerBannerCarouselSlide[] {
  if (!isProductsPageBannerCarousel(config)) {
    return [];
  }
  const slides = normalizeProductsPageBannerCarouselSlides(config.carouselSlides ?? []);
  const heroUrl = resolveProductsPageBannerHeroImageUrl(config);
  const placeholder = fallbackImage.trim();
  const heroDisplay = heroUrl
    ? normalizeMediaUrlForDisplay(heroUrl) || heroUrl
    : placeholder
      ? normalizeMediaUrlForDisplay(placeholder) || placeholder
      : '';

  const imageCarouselPerSlideOnly = isProductsPageBannerImageCarousel(config);

  return slides.map((slide) => {
    let imageUrl = resolveCarouselImageDisplayUrl(slide.imagePayload ?? slide.image);
    if (!imageUrl && !imageCarouselPerSlideOnly && heroUrl) {
      imageUrl = normalizeMediaUrlForDisplay(heroUrl) || heroUrl;
    }
    if (!imageUrl && placeholder && !imageCarouselPerSlideOnly) {
      imageUrl = normalizeMediaUrlForDisplay(placeholder) || placeholder;
    }
    if (!imageUrl && !imageCarouselPerSlideOnly) {
      imageUrl = heroDisplay;
    }
    const showTitle = slide.titleEnabled === true;
    const showDescription = slide.descriptionEnabled === true;
    const showCta = slide.ctaEnabled === true;
    const productItemCode = slide.productItemCode?.trim() || undefined;
    return {
      imageUrl,
      productItemCode,
      title: showTitle ? resolveMlText(slide.title, preferredLang).trim() : '',
      description: showDescription ? resolveMlText(slide.description, preferredLang).trim() : '',
      cta: showCta ? resolveMlText(slide.cta, preferredLang).trim() : '',
      showTitle,
      showDescription,
      showCta,
    };
  });
}

function resolveBannerSlideImageUrl(slide: ProductsPageBannerCarouselSlide): string {
  return resolveCarouselImageUrl(slide.imagePayload ?? slide.image).trim();
}

function carouselSlidesHaveDisplayableImage(slides: ProductsPageBannerCarouselSlide[]): boolean {
  return slides.some(
    (slide) =>
      resolveBannerSlideImageUrl(slide).length > 0 || !!slide.productItemCode?.trim(),
  );
}

export function resolveProductsPageCustomerBannerView(
  config: ProductsPageBannerConfig,
  preferredLang?: string,
): ProductsPageCustomerBannerView {
  const scopeActive = isProductsPageBannerActiveOnProductPage(config);
  const visible = config.visible !== false;

  let showTitle = false;
  let showDescription = false;
  let showCta = false;
  let title = '';
  let description = '';
  let cta = '';

  if (isProductsPageBannerCarousel(config)) {
    const slide = config.carouselSlides?.[0];
    if (slide) {
      showTitle = slide.titleEnabled === true;
      showDescription = slide.descriptionEnabled === true;
      showCta = slide.ctaEnabled === true;
      title = showTitle ? resolveMlText(slide.title, preferredLang) : '';
      description = showDescription ? resolveMlText(slide.description, preferredLang) : '';
      cta = showCta ? resolveMlText(slide.cta, preferredLang) : '';
    }
  } else {
    showTitle = config.titleEnabled === true;
    showDescription = config.descriptionEnabled === true;
    showCta = config.ctaEnabled === true;
    title = showTitle ? resolveMlText(config.title, preferredLang) : '';
    description = showDescription ? resolveMlText(config.description, preferredLang) : '';
    cta = showCta ? resolveMlText(config.cta, preferredLang) : '';
  }

  const imageUrl = resolveProductsPageBannerImageUrl(config).trim();

  return {
    visible,
    scopeActive,
    /** Visible + in scope is enough; customer UI still applies image fallbacks when URL is empty. */
    show: visible && scopeActive,
    imageUrl,
    title: title.trim(),
    description: description.trim(),
    cta: cta.trim(),
    showTitle,
    showDescription,
    showCta,
  };
}

export type ProductsPageBannerModeKey =
  | 'imageRegular'
  | 'imageCarousel'
  | 'productRegular'
  | 'productCarousel';

export const PRODUCTS_PAGE_BANNER_MODE_DRAFTS_PAYLOAD_KEY = 'bannerModeDrafts';
export const PRODUCTS_PAGE_BANNER_MOBILE_MODE_DRAFTS_PAYLOAD_KEY = 'bannerMobileModeDrafts';

const PRODUCTS_PAGE_BANNER_MODE_KEYS: ProductsPageBannerModeKey[] = [
  'imageRegular',
  'imageCarousel',
  'productRegular',
  'productCarousel',
];

export function resolveProductsPageBannerModeKeyFromConfig(
  config: ProductsPageBannerConfig,
): ProductsPageBannerModeKey {
  return resolveProductsPageBannerModeKeyFromTypes(config.contentType, config.displayType);
}

export function resolveProductsPageBannerModeKeyFromTypes(
  contentType: unknown,
  displayType: unknown,
): ProductsPageBannerModeKey {
  const isProduct = contentType === 'product';
  const isCarousel = displayType === 'carousel';
  if (isProduct) {
    return isCarousel ? 'productCarousel' : 'productRegular';
  }
  return isCarousel ? 'imageCarousel' : 'imageRegular';
}

function cloneProductsPageBannerConfig(config: ProductsPageBannerConfig): ProductsPageBannerConfig {
  return JSON.parse(JSON.stringify(config)) as ProductsPageBannerConfig;
}

function applyBannerModeKeyToConfig(
  key: ProductsPageBannerModeKey,
  config: ProductsPageBannerConfig,
): ProductsPageBannerConfig {
  const next = cloneProductsPageBannerConfig(config);
  switch (key) {
    case 'imageRegular':
      next.contentType = 'image';
      next.displayType = 'regular';
      break;
    case 'imageCarousel':
      next.contentType = 'image';
      next.displayType = 'carousel';
      break;
    case 'productRegular':
      next.contentType = 'product';
      next.displayType = 'regular';
      break;
    case 'productCarousel':
      next.contentType = 'product';
      next.displayType = 'carousel';
      break;
  }
  return next;
}

export function createDefaultProductsPageBannerForMode(
  key: ProductsPageBannerModeKey,
): ProductsPageBannerConfig {
  const base = cloneProductsPageBannerConfig(DEFAULT_PRODUCTS_PAGE_BANNER);
  const configured = applyBannerModeKeyToConfig(key, base);
  if (isProductsPageBannerCarousel(configured)) {
    configured.carouselSlides = createDefaultProductsPageBannerCarouselSlides().map((slide) => ({
      ...slide,
      title: { ...slide.title },
      description: { ...slide.description },
      cta: { ...slide.cta },
    }));
  } else {
    configured.carouselSlides = [];
  }
  return configured;
}

function copyBannerScopeFields(
  target: ProductsPageBannerConfig,
  source: ProductsPageBannerConfig,
): void {
  target.visible = source.visible;
  target.scope = source.scope;
  target.selectedPageKeys = [...source.selectedPageKeys];
}

function bootstrapProductsPageBannerModeDrafts(
  activeBanner: ProductsPageBannerConfig,
): Record<ProductsPageBannerModeKey, ProductsPageBannerConfig> {
  const drafts = {
    imageRegular: createDefaultProductsPageBannerForMode('imageRegular'),
    imageCarousel: createDefaultProductsPageBannerForMode('imageCarousel'),
    productRegular: createDefaultProductsPageBannerForMode('productRegular'),
    productCarousel: createDefaultProductsPageBannerForMode('productCarousel'),
  };
  const activeKey = resolveProductsPageBannerModeKeyFromConfig(activeBanner);
  drafts[activeKey] = cloneProductsPageBannerConfig(activeBanner);

  for (const key of PRODUCTS_PAGE_BANNER_MODE_KEYS) {
    copyBannerScopeFields(drafts[key], activeBanner);
  }

  const image = resolveCarouselImageUrl(activeBanner.image).trim();
  if (image) {
    drafts.imageRegular = {
      ...drafts.imageRegular,
      image,
      title: { ...activeBanner.title },
      description: { ...activeBanner.description },
      cta: { ...activeBanner.cta },
      titleEnabled: activeBanner.titleEnabled,
      descriptionEnabled: activeBanner.descriptionEnabled,
      ctaEnabled: activeBanner.ctaEnabled,
    };
  }

  const slides = normalizeProductsPageBannerCarouselSlides(activeBanner.carouselSlides ?? []);
  if (slides.some((slide) => resolveCarouselImageUrl(slide.image).trim())) {
    drafts.imageCarousel = {
      ...drafts.imageCarousel,
      carouselSlides: slides.map((slide) => ({
        ...slide,
        title: { ...slide.title },
        description: { ...slide.description },
        cta: { ...slide.cta },
      })),
    };
  }

  const productCode = activeBanner.productItemCode.trim();
  if (productCode) {
    drafts.productRegular = {
      ...drafts.productRegular,
      productItemCode: productCode,
      title: { ...activeBanner.title },
      description: { ...activeBanner.description },
      cta: { ...activeBanner.cta },
      titleEnabled: activeBanner.titleEnabled,
      descriptionEnabled: activeBanner.descriptionEnabled,
      ctaEnabled: activeBanner.ctaEnabled,
    };
  }

  if (slides.some((slide) => slide.productItemCode.trim())) {
    drafts.productCarousel = {
      ...drafts.productCarousel,
      carouselSlides: slides.map((slide) => ({
        ...slide,
        title: { ...slide.title },
        description: { ...slide.description },
        cta: { ...slide.cta },
      })),
    };
  }

  return drafts;
}

export function parseProductsPageBannerModeDrafts(
  payload: Record<string, unknown> | null | undefined,
  device: ProductsPageLayoutDevice,
  activeBanner: ProductsPageBannerConfig,
): Record<ProductsPageBannerModeKey, ProductsPageBannerConfig> {
  const payloadKey =
    device === 'mobile'
      ? PRODUCTS_PAGE_BANNER_MOBILE_MODE_DRAFTS_PAYLOAD_KEY
      : PRODUCTS_PAGE_BANNER_MODE_DRAFTS_PAYLOAD_KEY;
  const raw = payload?.[payloadKey];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return bootstrapProductsPageBannerModeDrafts(activeBanner);
  }
  const record = raw as Record<string, unknown>;
  const drafts = {} as Record<ProductsPageBannerModeKey, ProductsPageBannerConfig>;
  for (const key of PRODUCTS_PAGE_BANNER_MODE_KEYS) {
    const entry = record[key];
    drafts[key] =
      entry && typeof entry === 'object' && !Array.isArray(entry)
        ? parseBannerConfig(entry)
        : createDefaultProductsPageBannerForMode(key);
  }
  const activeKey = resolveProductsPageBannerModeKeyFromConfig(activeBanner);
  if (!record[activeKey]) {
    drafts[activeKey] = cloneProductsPageBannerConfig(activeBanner);
  }
  return drafts;
}

export function productsPageBannerModeDraftsToPayload(
  drafts: Record<ProductsPageBannerModeKey, ProductsPageBannerConfig>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const key of PRODUCTS_PAGE_BANNER_MODE_KEYS) {
    payload[key] = productsPageBannerToPayloadRecord(drafts[key]);
  }
  return payload;
}

export function productsPageBannerToPayloadRecord(
  config: ProductsPageBannerConfig,
  options?: { deviceImageConfigured?: boolean },
): Record<string, unknown> {
  return {
    visible: config.visible,
    contentType: config.contentType,
    displayType: config.displayType,
    scope: config.scope,
    selectedPageKeys: selectedPageKeysForScope(config.scope, [...config.selectedPageKeys]),
    image: resolveCarouselImageUrl(config.image),
    productItemCode: config.productItemCode,
    title: { ...config.title },
    description: { ...config.description },
    cta: { ...config.cta },
    titleEnabled: config.titleEnabled,
    descriptionEnabled: config.descriptionEnabled,
    ctaEnabled: config.ctaEnabled,
    carouselSlides: config.carouselSlides.map((slide) => ({
      image: resolveCarouselImageUrl(slide.image),
      productItemCode: slide.productItemCode,
      title: { ...slide.title },
      description: { ...slide.description },
      cta: { ...slide.cta },
      titleEnabled: slide.titleEnabled,
      descriptionEnabled: slide.descriptionEnabled,
      ctaEnabled: slide.ctaEnabled,
    })),
    ...(options?.deviceImageConfigured ? { deviceImageConfigured: true } : {}),
  };
}

export function mlRecordHasAnyText(record: MlTextRecord | null | undefined): boolean {
  if (!record) {
    return false;
  }
  return Object.values(record).some(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
}

export function mlRecordsEqual(
  left: MlTextRecord | null | undefined,
  right: MlTextRecord | null | undefined,
): boolean {
  const a = left ?? {};
  const b = right ?? {};
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if ((a[key] ?? '').trim() !== (b[key] ?? '').trim()) {
      return false;
    }
  }
  return true;
}

export function productsPageBannerImageIsConfiguredForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ProductsPageLayoutDevice,
): boolean {
  const key =
    device === 'mobile'
      ? PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY
      : PRODUCTS_PAGE_BANNER_PAYLOAD_KEY;
  const value = payload?.[key];
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as Record<string, unknown>)['deviceImageConfigured'] === true
  );
}

export function parseProductsPageBannerForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ProductsPageLayoutDevice = 'desktop',
): ProductsPageBannerConfig {
  if (device === 'mobile') {
    if (
      payload &&
      Object.prototype.hasOwnProperty.call(payload, PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY)
    ) {
      return parseBannerConfig(payload[PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY]);
    }
    return parseBannerConfig(undefined);
  }
  return parseBannerConfig(payload?.[PRODUCTS_PAGE_BANNER_PAYLOAD_KEY]);
}

export function parseProductsPageExploreForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ProductsPageLayoutDevice = 'desktop',
): ProductsPageExploreConfig {
  if (
    device === 'mobile' &&
    payload &&
    Object.prototype.hasOwnProperty.call(payload, PRODUCTS_PAGE_EXPLORE_MOBILE_PAYLOAD_KEY)
  ) {
    return parseExploreConfig(payload[PRODUCTS_PAGE_EXPLORE_MOBILE_PAYLOAD_KEY]);
  }
  return parseExploreConfig(payload?.[PRODUCTS_PAGE_EXPLORE_PAYLOAD_KEY]);
}

function parseExploreConfig(value: unknown): ProductsPageExploreConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_PRODUCTS_PAGE_EXPLORE };
  }
  const record = value as Record<string, unknown>;
  return {
    visible: record['visible'] !== false,
    title: parseMlRecord(record['title']),
  };
}

function parseRecentlyAddedConfig(value: unknown): ProductsPageRecentlyAddedConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_PRODUCTS_PAGE_RECENTLY_ADDED };
  }
  const record = value as Record<string, unknown>;
  return {
    visible: record['visible'] !== false,
    defaultOpen: record['defaultOpen'] === true,
    title: parseMlRecord(record['title']),
  };
}

/**
 * Payloads saved before the horizontal card view existed always stored
 * `horizontalCard: false`, because the editor then offered vertical + table only.
 * The persisted `order` lists every key the editor offered, so its contents tell a
 * deliberate "off" apart from a port that was never configurable.
 */
function horizontalCardWasConfigurable(source: Record<string, unknown>): boolean {
  const stored = source[ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY];
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return false;
  }
  const order = (stored as Record<string, unknown>)['order'];
  return Array.isArray(order) && order.includes('horizontalCard');
}

/** Those same payloads never ordered the horizontal card; keep it beside the vertical one. */
function withHorizontalCardBesideVerticalCard(order: ViewPortKey[]): ViewPortKey[] {
  const rest = order.filter((key) => key !== 'horizontalCard');
  const at = Math.max(rest.indexOf('verticalCard') + 1, 0);
  return [...rest.slice(0, at), 'horizontalCard', ...rest.slice(at)];
}

export function normalizeProductsPageLayoutConfig(
  payload: Record<string, unknown> | null | undefined,
): ProductsPageLayoutConfig {
  const source = payload ?? {};
  const horizontalWasConfigurable = horizontalCardWasConfigurable(source);
  const viewPorts = parseAllProductsViewPorts(source);
  if (!horizontalWasConfigurable) {
    viewPorts.horizontalCard = DEFAULT_PRODUCTS_PAGE_LAYOUT.allProductsViewPorts.horizontalCard;
  }
  const order = parseViewPortOrder(source, PRODUCTS_PAGE_VIEW_PORT_KEYS).filter((key) =>
    PRODUCTS_PAGE_VIEW_PORT_KEYS.includes(key),
  );
  const mergedOrder = [
    ...order,
    ...PRODUCTS_PAGE_VIEW_PORT_KEYS.filter((key) => !order.includes(key)),
  ];
  const resolvedOrder = horizontalWasConfigurable
    ? mergedOrder
    : withHorizontalCardBesideVerticalCard(mergedOrder);
  let defaultKey = parseDefaultViewPortKey(source, viewPorts, resolvedOrder);
  if (!PRODUCTS_PAGE_VIEW_PORT_KEYS.includes(defaultKey)) {
    defaultKey = resolvedOrder.find((key) => viewPorts[key]) ?? PRODUCTS_PAGE_VIEW_PORT_KEYS[0];
  }

  return {
    pageName: parseMlRecord(source[PRODUCTS_PAGE_NAME_PAYLOAD_KEY]),
    banner: parseBannerConfig(source[PRODUCTS_PAGE_BANNER_PAYLOAD_KEY]),
    exploreProducts: parseExploreConfig(source[PRODUCTS_PAGE_EXPLORE_PAYLOAD_KEY]),
    recentlyAdded: parseRecentlyAddedConfig(source[PRODUCTS_PAGE_RECENTLY_ADDED_PAYLOAD_KEY]),
    productsVisible: source[PRODUCTS_PAGE_PRODUCTS_VISIBLE_PAYLOAD_KEY] !== false,
    allProductsViewPorts: viewPorts,
    allProductsViewPortsDefault: defaultKey,
    allProductsViewPortsOrder: resolvedOrder,
  };
}

export function productsPageLayoutToPayload(config: ProductsPageLayoutConfig): Record<string, unknown> {
  const viewPorts = { ...config.allProductsViewPorts };
  return {
    [PRODUCTS_PAGE_NAME_PAYLOAD_KEY]: { ...config.pageName },
    [PRODUCTS_PAGE_BANNER_PAYLOAD_KEY]: productsPageBannerToPayloadRecord(config.banner, {
      deviceImageConfigured: true,
    }),
    [PRODUCTS_PAGE_EXPLORE_PAYLOAD_KEY]: {
      visible: config.exploreProducts.visible,
      title: { ...config.exploreProducts.title },
    },
    [PRODUCTS_PAGE_RECENTLY_ADDED_PAYLOAD_KEY]: {
      visible: config.recentlyAdded.visible,
      defaultOpen: config.recentlyAdded.defaultOpen,
      title: { ...config.recentlyAdded.title },
    },
    [PRODUCTS_PAGE_PRODUCTS_VISIBLE_PAYLOAD_KEY]: config.productsVisible,
    [ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY]: {
      ...viewPorts,
      order: config.allProductsViewPortsOrder?.length
        ? config.allProductsViewPortsOrder
        : [...PRODUCTS_PAGE_VIEW_PORT_KEYS],
      default: config.allProductsViewPortsDefault ?? PRODUCTS_PAGE_VIEW_PORT_KEYS[0],
    },
  };
}

export function resolveMlText(
  record: MlTextRecord | null | undefined,
  preferredLang?: string,
): string {
  if (!record) {
    return '';
  }
  const lang = (preferredLang || 'en').toLowerCase();
  const candidates = [lang, lang.split('-')[0], 'en', ...Object.keys(record)];
  for (const key of candidates) {
    const text = record[key];
    if (typeof text === 'string' && text.trim()) {
      return text.trim();
    }
  }
  return '';
}

export function defaultProductsPageViewPortsRecord(): Record<string, unknown> {
  return {
    ...DEFAULT_PRODUCTS_PAGE_LAYOUT.allProductsViewPorts,
    order: [...DEFAULT_PRODUCTS_PAGE_LAYOUT.allProductsViewPortsOrder],
    default: DEFAULT_PRODUCTS_PAGE_LAYOUT.allProductsViewPortsDefault,
  };
}
